import React, { useEffect, useMemo, useState } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import CustomInput from 'components/CustomInput/CustomInput';
import AlternativeSelect from 'components/AlternativeSelect/AlternativeSelect';
import { DepartmentsCell, PhoneNumberCell, TextCell } from 'components/Cells/Cells';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import { setToast } from 'redux/modules/toast/actions';
import { NumbersService } from 'services/numbers/numbers';
import { useDispatch } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { finalize } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { translate } from 'components/Translation';
import { capitalize } from 'utils/converters/capitalize';
import styles from 'assets/jss/material-dashboard-pro-react/components/addNumbersDialogStyle';
import { makeStyles } from '@material-ui/core';
import { BodyS } from 'components/Typography/Typography';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { SingleNumber } from 'utils/api/models/numbers/singleNumber';
import { IUserData } from 'utils/api/models/users/corpUserDetails';
import NumberInfo from './subcomponents/NumberInfo';
import CustomTable from 'components/CustomTable/CustomTable';

// @ts-ignore
const useStyles = makeStyles(styles);

type selectedService = 'all' | 'mobilePostpaid' | 'mobilePrepaid' | 'internetPostpaid' | 'fixPostpaid';

interface IAddNumberDialogProps {
	isDialogOpen: boolean;
	setIsDialogOpen: (isOpen: boolean) => void;
	userId: string;
	numbers: SingleNumber[];
	loading: boolean;
	numbersSuccessfullyAdded: (numbers: string[]) => void;
	departments: SingleDepartment[];
	userDepartment: IUserData;
}

const AddNumberDialog = ({
	isDialogOpen,
	setIsDialogOpen,
	userId,
	numbers,
	loading,
	numbersSuccessfullyAdded,
	departments,
	userDepartment,
}: IAddNumberDialogProps) => {
	const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
	const [searchInputValue, setSearchInputValue] = useState<string>('');
	const [selectedServices, setSelectedServices] = useState<selectedService[]>(['all']);
	const [filteredNumbers, setFilteredNumbers] = useState<SingleNumber[]>([]);
	const [confirmationDialog, setConfirmationDialog] = useState<boolean>(false);
	const dispatch = useDispatch();
	const classes = useStyles();

	const services = [
		{
			name: translate('all'),
			id: 'all',
		},
		{
			name: translate('numbers.mobile-postpaid'),
			id: 'mobilePostpaid',
		},
		{
			name: translate('numbers.mobile-prepaid'),
			id: 'mobilePrepaid',
		},
		{
			name: translate('numbers.fbb'),
			id: 'internetPostpaid',
		},
		{
			name: translate('numbers.fixed-line'),
			id: 'fixPostpaid',
		},
	];

	const findDepartment = (departmentId: string | undefined) => {
		return departments.find((department) => department.deptId === departmentId);
	};

	useEffect(() => {
		const all = selectedServices.indexOf('all') >= 0;
		setFilteredNumbers(
			numbers
				.filter((number) => {
					return (
						all ||
						selectedServices.indexOf(
							`${number.accountType?.toLowerCase()}${capitalize(number.billingType)}` as selectedService
						) >= 0
					);
				})
				.filter((number) => number.accountNum.indexOf(searchInputValue) >= 0)
		);
	}, [numbers, searchInputValue, selectedServices]);

	const clearDialog = (): void => {
		setSearchInputValue('');
		setSelectedServices(['all']);
		setSelectedIndexes([]);
	};

	const hideDialog = (): void => {
		setIsDialogOpen(false);
		clearDialog();
	};

	const selectedNumbers = filteredNumbers
		.filter((_, index) => selectedIndexes.includes(index))
		.map((num) => num.accountNum);

	const addNumbers = (): void => {
		setIsDialogOpen(false);
		setConfirmationDialog(false);
		dispatch(showLoadingIndicator());
		NumbersService.assignNumbersForCorpUserRestPost({
			userId,
			numbers: selectedNumbers,
		})
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
				})
			)
			.subscribe(
				({ response }) => {
					if (response.assignNumersForCorpUserResponse.result) {
						if (numbersSuccessfullyAdded) {
							numbersSuccessfullyAdded([...selectedNumbers]);
						}
						dispatch(
							setToast({
								error: false,
								label: translate('numbers.added'),
								actionType: translate('added'),
							})
						);
						clearDialog();
					}
				},
				(error) => throwError(error)
			);
	};

	const selectedNumbersLength: string = selectedNumbers.length > 0 ? ` (${selectedNumbers.length})` : '';

	const serviceType = (item: SingleNumber) => {
		return translate(`numbers.${item.accountType?.toLowerCase()}-${item.billingType?.toLowerCase()}`);
	};

	const selectedItem = (() => {
		if (selectedServices.indexOf('all') >= 0) {
			return translate('all');
		}
		if (selectedServices.length === 0) {
			return translate('services');
		}
		if (selectedServices.length === 1) {
			const service = services.find((_service) => _service.id === selectedServices[0]);
			if (service) {
				return service.name;
			}
		}
		return translate('multiple.selected');
	})();

	const showRemoveDepartmentsConfirmation = useMemo(() => {
		if (!userDepartment) {
			const _numbers = numbers.filter((number) => selectedNumbers.indexOf(number.accountNum) >= 0);
			return _numbers
				.filter((number) => number.departmentId)
				.map((number) => ({
					...number,
					department: findDepartment(number.departmentId),
				}));
		}
		return [];
		// eslint-disable-next-line
	}, [userDepartment, numbers, selectedNumbers]);

	const showChangeDepartmentsConfirmation = useMemo(() => {
		if (userDepartment) {
			const _numbers = numbers.filter((number) => selectedNumbers.indexOf(number.accountNum) >= 0);
			return _numbers
				.filter((number) => number.departmentId && number.departmentId !== userDepartment.departmentId)
				.map((number) => ({
					...number,
					department: findDepartment(number.departmentId),
				}));
		}
		return [];
		// eslint-disable-next-line
	}, [userDepartment, numbers, selectedNumbers]);

	return (
		<>
			<CustomDialog
				isOpen={isDialogOpen}
				hideDialog={hideDialog}
				width={700}
				header={translate('add.numbers')}
				rightSideButtons={[
					{
						text: translate('cancel'),
						onClick: hideDialog,
						color: 'tertiary-light',
					},
					{
						text: `${translate('add')}${selectedNumbersLength}`,
						onClick: () => {
							if (
								showChangeDepartmentsConfirmation.length > 0 ||
								showRemoveDepartmentsConfirmation.length > 0
							) {
								setIsDialogOpen(false);
								setConfirmationDialog(true);
							} else {
								addNumbers();
							}
						},
						color: 'primary-light',
						disabled: selectedNumbers.length <= 0,
					},
				]}
				dialogBody={
					<div className={classes.dialogBodyWrapper}>
						<div className={classes.filters}>
							<div>
								<CustomInput
									labelText={translate('numbers')}
									formControlProps={{
										fullWidth: true,
									}}
									inputProps={{
										value: searchInputValue,
										onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
											setSearchInputValue(event.target.value);
										},
										endAdornment: (
											<InputAdornment position="end">
												<SearchIcon className={classes.searchIcon} />
											</InputAdornment>
										),
									}}
								/>
							</div>
							<div>
								<AlternativeSelect
									label={translate('table.service.type')}
									value={
										<div className={classes.selectedItem}>
											<BodyS disableMargin>{selectedItem}</BodyS>
										</div>
									}
									onClick={(service) => {
										const index = selectedServices.indexOf(service.id);
										if (index >= 0) {
											if (service.id === 'all') {
												setSelectedServices([]);
											} else {
												const _selectedServices = [...selectedServices];
												_selectedServices.splice(index, 1);
												setSelectedServices(_selectedServices);
											}
										} else {
											if (service.id === 'all') {
												setSelectedServices(['all']);
											} else {
												const index = selectedServices.indexOf('all');
												const _selectedServices = [...selectedServices];
												if (index >= 0) {
													_selectedServices.splice(index, 1);
												}
												setSelectedServices([..._selectedServices, service.id]);
											}
										}
									}}
									items={services}
									selectedItems={selectedServices}
								/>
							</div>
						</div>
						<CustomTable
							withHeader
							withCheckbox
							selected={selectedIndexes}
							onSelect={(index) => setSelectedIndexes(index)}
							total={(filteredNumbers || [])?.length}
							tableProps={{
								data: filteredNumbers,
								columns: [
									{
										Header: translate('table.phone.number'),
										width: 150,
										accessor: 'accountNum',
										Cell: ({ original }: { original: SingleNumber }) => (
											<PhoneNumberCell text={original.accountNum} />
										),
									},
									{
										accessor: 'billingType',
										Header: translate('table.service.type'),
										width: 200,
										Cell: ({ original }: { original: SingleNumber }) => (
											<TextCell text={serviceType(original)} />
										),
									},
									{
										accessor: 'departmentId',
										Header: translate('table.department'),
										width: 200,
										Cell: ({ original }: { original: SingleNumber }) => (
											<DepartmentsCell
												maxBadgeWidth={200}
												maxNumberOfBadges={1}
												departments={
													original.departmentId
														? ([findDepartment(original.departmentId)].filter(
																Boolean
														  ) as SingleDepartment[])
														: []
												}
											/>
										),
									},
								],
								defaultPageSize: 5,
							}}
						/>
					</div>
				}
			/>
			<ConfirmationDialog
				isOpen={confirmationDialog}
				hideDialog={() => setConfirmationDialog(false)}
				header={
					showChangeDepartmentsConfirmation.length > 0
						? `${translate('departments.change.department')}?`
						: translate('departments.no.department')
				}
				dialogBody={
					<div>
						{showChangeDepartmentsConfirmation.length > 0 ? (
							<div>
								<BodyS>{translate('users.numbers.have.to.be.assigned')}</BodyS>
								<BodyS>{translate('users.do.you.want.to.change.department')}</BodyS>
								<div className={classes.numbersWrapper}>
									{showChangeDepartmentsConfirmation.map((number) => (
										<NumberInfo
											key={number.accountNum}
											newDepartment={userDepartment}
											phoneNumber={number.accountNum}
											prevDepartment={number.department}
										/>
									))}
								</div>
							</div>
						) : (
							<div>
								<BodyS>{translate('users.user.is.not.assigned')}</BodyS>
								<BodyS>{translate('users.do.you.want.to.add.numbers')}</BodyS>
								<div className={classes.numbersWrapper}>
									{showRemoveDepartmentsConfirmation.map((number) => (
										<NumberInfo
											key={number.accountNum}
											phoneNumber={number.accountNum}
											prevDepartment={number.department}
										/>
									))}
								</div>
							</div>
						)}
					</div>
				}
				buttons={[
					{
						text: translate('cancel'),
						onClick: () => {
							setConfirmationDialog(false);
							setIsDialogOpen(true);
						},
						color: 'tertiary-light',
					},
					{
						text: showChangeDepartmentsConfirmation.length > 0 ? translate('change') : translate('add'),
						onClick: addNumbers,
						color: 'primary-light',
					},
				]}
			/>
		</>
	);
};

export default AddNumberDialog;
